import { observable, computed, action, makeObservable } from 'mobx'
import { task } from 'mobx-task'
import { Subject, firstValueFrom } from 'rxjs'
import { concatMap, find, share, switchMap } from 'rxjs/operators'
import { AlertFactory } from 'alerts/Alert'
import Toggle from 'infra/Toggle'
import { CursorPaged, CursorPageQuery } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'

/**
 * Powers Skill and Role management. They both
 * work the same way.
 */
export default class SelectionViewModel<T> {
  /**
   * Subject for change requests.
   */
  private requestedOperations = new Subject<Operation<T>>()

  /**
   * Pipeline for making changes, ensures only one
   * request is happening at once.
   * Emits the requested change.
   */
  private completedOperations$ = this.requestedOperations.pipe(
    concatMap((change) => this.performOperation(change)),
    share(),
  )

  /**
   * Search text.
   */
  @observable searchText = ''

  /**
   * Whether we are showing the editor.
   */
  showingEditor = new Toggle(false)

  /**
   * Scroll provider used to manage infinite scrolling state.
   */
  scrollProvider = new InfiniteScrollProvider({
    fetchItems: (params) => this.adapter.fetch(params),
    limit: 100,
  })

  /**
   * Constructor.
   *
   * @param adapter the adapter to use
   */
  constructor(private adapter: SelectionViewModelPort<T>) {
    makeObservable(this)
    // When we show the editor, fetch the items, but only once.
    this.showingEditor.isEnabled$
      .pipe(
        find((isShowing) => isShowing),
        switchMap(() => this.scrollProvider.fetch()),
      )
      .subscribe()
  }

  /**
   * All items from the scroll provider.
   */
  @computed
  get allItems(): T[] {
    return this.scrollProvider.items
  }

  /**
   * The selected items.
   */
  @computed
  get selectedItems(): T[] {
    return this.adapter.selection
  }

  /**
   * Gets the filtered items.
   *
   * A value is included in the matching items
   * if all parts of the search text have a partial
   * match with any parts of the item
   *
   * E.g
   * items = [['Something', 'to look for'], ['slow sound'], ['slow']]
   * searchText = "so lo"
   *
   * Will match return [[Something', 'to look for'], ['slow sound']]
   */
  @computed
  get filteredItems() {
    const normalized = this.searchText.toLowerCase().trim()
    if (!normalized) {
      return this.allItems
    }

    const searchParts = normalized.split(' ')
    return this.allItems.filter((item) => {
      const itemParts = this.adapter
        .getTextSearchValues(item)
        .filter((v) => v !== '')
        .map((p) => p.toLocaleLowerCase())

      let matchingParts = 0
      for (const searchPart of searchParts) {
        const matches = itemParts.some((ip) => ip.includes(searchPart))
        if (matches) {
          matchingParts++
        }
      }

      // Include in results if all search parts had a partial match
      return matchingParts === searchParts.length
    })
  }

  /**
   * Check if the item is selected.
   * Used by the all-list, shouldn't be called until items have been fetched.
   * @param item
   */
  isSelected(item: T) {
    return this.selectedItems.includes(item)
  }

  /**
   * Gets item ID.
   *
   * @param item
   */
  getItemId(item: T) {
    return this.adapter.getItemId(item)
  }

  /**
   * Whether we are allowed to perform an operation on this item.
   *
   * @param item
   */
  canUpdate(item: T) {
    return this.adapter.canUpdate(item)
  }

  /**
   * Sets the items.
   *
   * @param searchText
   */
  @action
  setSearchText(searchText: string) {
    this.searchText = searchText
  }

  /**
   * Adds an item.
   */
  add = task.resolved(async (item: T) => {
    return this.queueOp({
      item,
      op: SelectionOperation.Add,
    })
  })

  /**
   * Removes an item.
   */
  remove = task.resolved(async (item: T) => {
    return this.queueOp({
      item,
      op: SelectionOperation.Remove,
    })
  })

  /**
   * Toggle the selection.
   */
  toggle = (item: T) => {
    return this.isSelected(item) ? this.remove(item) : this.add(item)
  }

  /**
   * Performs the operation.
   * @param change
   */
  async performOperation(change: Operation<T>) {
    try {
      await (change.op === SelectionOperation.Add
        ? this.adapter.add(change.item)
        : this.adapter.remove(change.item))
    } catch (err) {
      this.adapter.newAlert().fromError(err).show()
      console.error(err)
    }
    return change
  }

  /**
   * Queues an operation, returns a Promise that resolves when it is done.
   *
   * @param operation
   */
  private queueOp(operation: Operation<T>) {
    const promise = firstValueFrom(
      this.completedOperations$.pipe(find((c) => c === operation)),
      { defaultValue: undefined },
    )
    this.requestedOperations.next(operation)
    return promise
  }
}

/**
 * Port for providing functionality to the selection VM.
 */
export interface SelectionViewModelPort<T> {
  /**
   * Used for creating an alert.
   */
  newAlert: AlertFactory
  /**
   * Delegate for getting the item ID.
   * @param item
   */
  getItemId(item: T): string
  /**
   * Delegate for whether we are allowed to change the item.
   * @param item
   */
  canUpdate(item: T): boolean
  /**
   * Delegate for fetching all the available items.
   */
  fetch(query: CursorPageQuery): Promise<CursorPaged<T>>
  /**
   * The currently selected items.
   */
  readonly selection: T[]
  /**
   * Delegate for adding an item.
   * The computed selection should change after this.
   *
   * @param item
   */
  add(item: T): Promise<unknown>
  /**
   * Delegate for removing an item.
   * The computed selection should change after this.
   *
   * @param item
   */
  remove(item: T): Promise<unknown>
  /**
   * Delegate for gettng an array of text values that can be searched.
   *
   * @param item
   */
  getTextSearchValues(item: T): string[]
}

/**
 * Selection operation.
 */
const enum SelectionOperation {
  Add,
  Remove,
}

/**
 * Requested change.
 */
interface Operation<T> {
  op: SelectionOperation
  item: T
}
